/* A row's fields as a form, one per column: what the row panel shows for a new row and for
   one being changed. A link picks rows of its table and a person picks one of the agent's
   people, both by search; a verified-only email asks who verified it. */
import React, { useId, useState } from "react";
import { X } from "lucide-react";

import { useSpaceRows } from "@/shared/api/spaces";
import { RowList } from "@/shared/components/settings-atoms";
import { IconButton } from "@/shared/components/ui-kit";
import { Switch } from "@/shared/components/ui/switch";
import { inputCls } from "@/shared/components/task-atoms";
import { cn } from "@/shared/lib/cn";

import { LinkChip } from "./table/Cell";
import { ICONS } from "./table/columns";

import type { SpaceColumn, SpaceLinked, SpaceTable, SpaceValue } from "@/shared/types";

export type FieldValues = Record<string, SpaceValue>;

const fieldCls = `${inputCls} min-w-0`;  // the app's small input (shared/components/task-atoms)

export default function RowFields({ agentId, columns, tables, values, verified, people, onValue, onVerified }: {
  agentId: string;
  columns: SpaceColumn[];
  /** Every table of the space: a link's rows are named by their table's first column. */
  tables: SpaceTable[];
  values: FieldValues;
  verified: Record<string, string>;
  people: Record<string, string | null>;
  onValue: (key: string, value: SpaceValue) => void;
  onVerified: (key: string, by: string) => void;
}) {
  const titleOf = (tableId?: string) => tables.find((t) => t.id === tableId)?.columns[0]?.key ?? "name";
  return (
    <RowList>
      {columns.filter((column) => column.type !== "count").map((column) => {
        const Icon = ICONS[column.type];
        return (
          <div key={column.key} className="flex items-start gap-3 py-2 min-h-[44px]">
            <span className="w-[104px] shrink-0 pt-[7px] flex items-center gap-1.5 text-[12.5px] text-[var(--text-tertiary)]">
              <Icon className="w-3.5 h-3.5 shrink-0 text-[var(--text-quaternary)]" strokeWidth={1.75} />
              <span className="truncate">{column.name}</span>
              {column.rules.includes("required") && <span aria-label="required" className="text-[var(--text-quaternary)]">*</span>}
            </span>
            <span className="flex-1 min-w-0 space-y-1.5">
              <Field
                agentId={agentId}
                column={column}
                value={values[column.key] ?? null}
                people={people}
                titleKey={column.type === "link" ? titleOf(column.options.table) : "name"}
                onValue={(value) => onValue(column.key, value)}
              />
              {column.rules.includes("verified_only") && (
                <label className="flex items-center gap-2">
                  <span className="shrink-0 text-[12px] text-[var(--text-tertiary)]">Verified by</span>
                  <input
                    value={verified[column.key] ?? ""}
                    onChange={(e) => onVerified(column.key, e.target.value)}
                    placeholder="e.g. apollo"
                    aria-label={`${column.name}: verified by`}
                    className={cn(fieldCls, "h-7 text-[12px]")}
                  />
                </label>
              )}
            </span>
          </div>
        );
      })}
    </RowList>
  );
}

function Field({ agentId, column, value, people, titleKey, onValue }: {
  agentId: string;
  column: SpaceColumn;
  /** The column naming the rows a link or person picks from. */
  titleKey: string;
  value: SpaceValue;
  people: Record<string, string | null>;
  onValue: (value: SpaceValue) => void;
}) {
  const text = value === null || value === undefined || Array.isArray(value) ? "" : String(value);
  switch (column.type) {
    case "boolean":
      return <Switch checked={value === true} onCheckedChange={onValue} aria-label={column.name} className="mt-1.5" />;
    case "choice":
      return (
        <select value={text} onChange={(e) => onValue(e.target.value || null)} aria-label={column.name} className={fieldCls}>
          <option value="">—</option>
          {column.options.choices?.map((choice) => <option key={choice.name} value={choice.name}>{choice.name}</option>)}
        </select>
      );
    case "long_text":
      return <textarea value={text} onChange={(e) => onValue(e.target.value)} rows={4} aria-label={column.name} className={cn(fieldCls, "h-auto py-2 resize-y")} />;
    case "link":
      return (
        <RowPicker
          agentId={agentId}
          tableId={column.options.table ?? ""}
          titleKey={titleKey}
          picked={Array.isArray(value) ? value : []}
          many={column.options.many !== false}
          label={column.name}
          onPicked={onValue}
        />
      );
    case "person":
      return (
        <RowPicker
          agentId={agentId}
          tableId="people"
          titleKey={titleKey}
          picked={text ? [{ id: text, label: people[text] ?? "Someone" }] : []}
          many={false}
          label={column.name}
          onPicked={(picked) => onValue(picked[0]?.id ?? null)}
        />
      );
    default:
      return (
        <input
          value={text}
          onChange={(e) => onValue(e.target.value)}
          type={column.type === "date" ? "date" : "text"}
          inputMode={column.type === "number" ? "decimal" : undefined}
          aria-label={column.name}
          className={fieldCls}
        />
      );
  }
}

/** Rows of a table picked by search: the picked ones as chips, the matches under the box,
    each named by its table's first column. Arrows move through the matches, Enter picks one,
    Escape closes them. */
function RowPicker({ agentId, tableId, titleKey, picked, many, label, onPicked }: {
  agentId: string;
  tableId: string;
  /** The key of the column that names the table's rows. */
  titleKey: string;
  picked: SpaceLinked[];
  many: boolean;
  label: string;
  onPicked: (picked: SpaceLinked[]) => void;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [lit, setLit] = useState(0);
  const listId = useId();
  const { data } = useSpaceRows(agentId, open ? tableId : null, { search: search.trim() || undefined, limit: 8 });
  const ids = new Set(picked.map((row) => row.id));
  const matches = (data?.rows ?? []).filter((row) => !ids.has(row.id)).map((row) => ({
    id: row.id,
    label: String(row.values[titleKey] ?? "") || "Untitled",
  }));
  const pick = (row: SpaceLinked) => {
    onPicked(many ? [...picked, row] : [row]);
    setSearch("");
    setLit(0);
  };
  const onKey = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      const step = event.key === "ArrowDown" ? 1 : -1;
      setLit((at) => (matches.length ? (at + step + matches.length) % matches.length : 0));
    } else if (event.key === "Enter" && open && matches[lit]) {
      event.preventDefault();
      pick(matches[lit]);
    } else if (event.key === "Escape" && open) {
      event.stopPropagation();
      setOpen(false);
    }
  };
  return (
    <div className="space-y-1.5">
      {picked.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {picked.map((row) => (
            <span key={row.id} className="inline-flex items-center gap-0.5">
              <LinkChip linked={row} />
              <IconButton aria-label={`Remove ${row.label ?? "row"}`} onClick={() => onPicked(picked.filter((p) => p.id !== row.id))} className="!w-5 !h-5 !rounded-[6px]">
                <X className="w-3 h-3" strokeWidth={2} />
              </IconButton>
            </span>
          ))}
        </div>
      )}
      {(many || picked.length === 0) && (
        <div className="relative">
          <input
            role="combobox"
            aria-expanded={open && matches.length > 0}
            aria-controls={listId}
            aria-activedescendant={open && matches[lit] ? `${listId}-${lit}` : undefined}
            aria-autocomplete="list"
            value={search}
            onFocus={() => setOpen(true)}
            onBlur={() => window.setTimeout(() => setOpen(false), 120)}
            onChange={(e) => {
              setSearch(e.target.value);
              setLit(0);
              setOpen(true);
            }}
            onKeyDown={onKey}
            placeholder={`Find in ${tableId === "people" ? "People" : label}`}
            aria-label={`${label}: find a row`}
            className={fieldCls}
          />
          {open && matches.length > 0 && (
            <div id={listId} role="listbox" className="absolute z-20 left-0 right-0 mt-1 p-1 rounded-[12px] bg-[var(--surface)] border border-[var(--hairline-strong)] shadow-menu">
              {matches.map((row, index) => (
                <button
                  key={row.id}
                  id={`${listId}-${index}`}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  aria-selected={index === lit}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setLit(index)}
                  onClick={() => pick(row)}
                  className={cn(
                    "press w-full h-8 px-2 flex items-center rounded-[8px] text-left text-[13px] text-[var(--text-primary)]",
                    index === lit && "bg-[var(--accent-hover)]",
                  )}
                >
                  <span className="truncate">{row.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
